/**
 * The fan's loyalty session as component state: whether the API is usable, and their club.
 *
 * The one place either question is answered. Both blocks read it, and neither read costs a request:
 * `session.js` gets the bearer from the Auth0 SDK's own cache and the club out of the page.
 *
 * **`authenticated` is a narrower question than "is the fan signed in", and the difference is
 * real.** It reports whether there is a live loyalty-audience bearer — a credential, not an
 * impression. A fan the SDK resolved perfectly can still fail to obtain one, which is
 * [D-046](../../../../docs/decisions.md)'s split state, so any surface offering an ACTION has to
 * ask this question rather than `useSignedIn()`.
 *
 * **Tri-state, and the `undefined` is load-bearing.** `undefined` means the answer has not landed;
 * `{ team: null }` means it has, and this fan has no club. `ProfileHeaderBlock` renders no hero for
 * the first and an unbranded band for the second, because flashing the unbranded band before the
 * fan's club arrives is the defect that branch exists to prevent. This is why `session.js`'s
 * `getSession()` is `ready`-backed rather than read off `state()`, which answers `null` for both.
 *
 * **It takes no argument any more, and re-reads on every change of its own.** It used to be handed
 * `useSignedIn()`'s boolean as a cue, which was enough while the only transition that mattered was
 * signed-out → signed-in. `window.urcAuth.subscribe` reports every observable change, including one
 * fan replacing another with `signedIn` true throughout — and a boolean cue cannot see that, so the
 * second fan would have been shown the first fan's crest until the next navigation.
 */

import { useEffect, useState } from 'preact/hooks';
import { getSession, refreshSession } from './session';
import { subscribeAuth } from './urcAuth';

/**
 * @return {Object|undefined} The session, or undefined until the first read lands. Never rejects.
 */
export function useLoyaltySession() {
	const [ session, setSession ] = useState( undefined );

	useEffect( () => {
		let alive = true;
		let asked = false;

		// Two names for the same read. Kept because the distinction is what a reader wants here: a
		// later change of the fan is a RE-read, not a first read.
		const read = () => {
			const request = asked ? refreshSession() : getSession();

			asked = true;

			request.then( value => {
				if ( alive ) {
					setSession( value );
				}
			} );
		};

		// Fires immediately when the SDK has already settled, so the common case is one read.
		const stop = subscribeAuth( read );

		return () => {
			alive = false;
			stop();
		};
	}, [] );

	return session;
}
